import { useState } from 'react';

import { Button, Divider, Grid, Menu, MenuItem, Stack } from '@mui/material';

import PlaceholderImage from '@/assets/images/pngs/bolo.png';
import { DataGrid, SideModal, ViewItem } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { SideModalTitle } from '@/features/properties/components/sidemodal-content/SideModalTitle';
import {
  towingCompaniesDummyData,
  TowingManagementTableColumns,
} from '@/pages/Tools/TowingManagement/data';
import { TowingCompanyFormPanel } from '@/pages/Tools/TowingManagement/Panels/TowingCompanySidePanel';
import { useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

export const TowingCompanyTable = () => {
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState<any | null>(null);
  const openMenu = Boolean(anchorEl);
  const [companies, setCompanies] = useState(towingCompaniesDummyData);
  const [refreshKey, setRefreshKey] = useState(0);
  const { navigateWithSearch } = useSearchPersist();

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: any,
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCompany(row);
  };

  const { user } = useUserStore();

  const handleMenuClose = () => setAnchorEl(null);

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `towingCompanies-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return TowingManagementTableColumns;
      }

      return mergeColumnSettings(TowingManagementTableColumns, savedSettings);
    },
  );

  const sortCompanies = (toSort: typeof towingCompaniesDummyData) => {
    // Sort by status, active first / inactive last
    // Set the position value to the position as requested
    return toSort
      .sort((a, b) => a.status.localeCompare(b.status)) // a > i
      .map((company, i) => ({
        ...company,
        position: i + 1,
      }));
  };

  const columns = useDataGridColumns({
    type: 'towingCompanies',
    columnSettings,
    openMenu: handleMenuOpen,
  });
  return (
    <Stack spacing={2} flex={1}>
      <DataGrid
        key={refreshKey}
        name="towingCompanies"
        persistSearch
        clientMode
        clientRows={sortCompanies(companies)}
        columns={columns}
        columnSettings={columnSettings}
        setColumnSettings={setColumnSettings}
        onRowClick={(params) =>
          navigateWithSearch(
            `/tools/towing-management/towing-company/${params.row.id}`,
          )
        }
        autoPageSize
        getRowId={(row) => row.id}
        cta={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setCompanyToEdit(null);
              setIsFormOpen(true);
            }}
          >
            Add Company
          </Button>
        }
      />

      <Divider sx={{ mt: 2 }} />

      <SideModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
      >
        <>
          <Stack sx={{ p: 2, pt: 0 }}>
            <SideModalTitle>Towing Company Details</SideModalTitle>
          </Stack>
          <Grid container spacing={2} sx={{ p: 2, pt: 0 }}>
            <Grid item xs={5}>
              <img
                src={PlaceholderImage}
                alt="Tow company visual"
                style={{ width: '100%', height: '300px' }}
              />
            </Grid>
            <Grid item xs={7}>
              <ViewItem label="Company Name" value={selectedCompany.company} />
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <ViewItem label="Status" value={selectedCompany.status} />
                </Grid>
                <Grid item xs={4}>
                  <ViewItem
                    label="Last Used"
                    value={selectedCompany.lastUsed}
                  />
                </Grid>
                <Grid item xs={4}>
                  <ViewItem label="Phone" value={selectedCompany.phoneNumber} />
                </Grid>
              </Grid>
              <Divider sx={{ my: 1 }} />
              <ViewItem
                label="Service Area"
                value={selectedCompany.serviceArea}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <ViewItem
                label="Address"
                value={selectedCompany.address}
                sx={{ '.value': { textTransform: 'none', flex: 'unset' } }}
              />
            </Grid>
          </Grid>

          <Stack sx={{ p: 2, mt: 'auto' }}>
            <Divider sx={{ my: 3 }} />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setIsViewModalOpen(false)}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </>
      </SideModal>

      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        MenuListProps={{ 'aria-labelledby': 'actions-menu-button' }}
      >
        <MenuItem
          onClick={() => {
            navigateWithSearch(
              `/tools/towing-management/towing-company/${selectedCompany.id}`,
            );
            handleMenuClose();
          }}
        >
          View
        </MenuItem>

        <MenuItem
          onClick={() => {
            setCompanyToEdit({
              companyName: selectedCompany.company,
              contactPerson: selectedCompany.contact,
              primaryPhone: selectedCompany.phoneNumber,
              secondaryPhone: selectedCompany.secondaryPhone || '',
              email: selectedCompany.email || '',
              license: selectedCompany.license || '',
              address: selectedCompany.address || '',
              serviceArea: selectedCompany.serviceArea || '',
              isActive: selectedCompany.status === 'Active',
            });
            setIsFormOpen(true);
            setIsViewModalOpen(false);
            handleMenuClose();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCompanies((prev) => {
              const updated = prev.map((item) =>
                item.id === selectedCompany.id
                  ? {
                      ...item,
                      status: item.status === 'Active' ? 'Inactive' : 'Active',
                    }
                  : item,
              );
              const updatedCompany = updated.find(
                (i) => i.id === selectedCompany.id,
              );
              if (updatedCompany) setSelectedCompany(updatedCompany);
              return sortCompanies(updated);
            });
            setRefreshKey((prev) => prev + 1);
            handleMenuClose();
          }}
        >
          {selectedCompany.status === 'Active' ? 'Deactivate' : 'Activate'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCompanies((prev) => {
              const index = prev.findIndex(
                (item) => item.id === selectedCompany.id,
              );
              if (index === -1) return prev;

              const updatedList = [...prev];
              const [usedCompany] = updatedList.splice(index, 1);
              updatedList.push(usedCompany);

              setSelectedCompany(usedCompany);
              return sortCompanies(updatedList);
            });
            setRefreshKey((prev) => prev + 1);
            handleMenuClose();
          }}
        >
          Mark as Used
        </MenuItem>
      </Menu>

      <TowingCompanyFormPanel
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={() => {
          setIsFormOpen(false);
        }}
        defaultValues={companyToEdit || undefined}
        mode={companyToEdit ? 'edit' : 'add'}
      />
    </Stack>
  );
};
